/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import {
  ProductDetail,
  STOItem,
  STOProcessedItem,
  STOReceiveItemState,
} from 'commons/types';
import { ReducerKeys } from 'redux-stores/enums/reducerKey.enum';

const initialState: STOReceiveItemState = {
  assignedSTOItems: [],
  stoItemSKUList: [],
  stoItems: {},
  stoReceiveItem: null,
  binRecommendationInfo: [
    {
      bin: '-',
      bin_type: '-',
      inventory_number: '-',
      quantity: 0,
    } as ProductDetail,
  ],
};

const stoReceiveItemSlice = createSlice({
  name: ReducerKeys.STOReceiveItem,
  initialState,
  reducers: {
    setAssignedSTOReceiveItem(
      state: STOReceiveItemState,
      action: { type: string; payload: STOItem[] },
    ) {
      state.assignedSTOItems = action.payload;
    },
    addAssignedSTOReceiveItem(
      state: STOReceiveItemState,
      action: { type: string; payload: STOItem },
    ) {
      state.assignedSTOItems.push(action.payload);
    },
    removeAssignedSTOReceiveItem(
      state: STOReceiveItemState,
      action: { type: string; payload: STOItem },
    ) {
      const updatedTasks = state.assignedSTOItems.filter(
        (task) =>
          task.sto_number !== action.payload.sto_number ||
          task.sku_code !== action.payload.sku_code,
      );
      state.assignedSTOItems = updatedTasks;
    },
    updateAssignedSTOReceiveItem(
      state: STOReceiveItemState,
      action: { type: string; payload: STOItem },
    ) {
      const updatedItems = state.assignedSTOItems.map((item) =>
        item.sto_number === action.payload.sto_number ? action.payload : item,
      );
      state.assignedSTOItems = updatedItems;
    },
    prepareSTOReceiveItems(
      state: STOReceiveItemState,
      action: { type: string; payload: STOItem[] | null | undefined },
    ) {
      const { payload } = action;
      if (payload && payload.length > 0) {
        state.assignedSTOItems = payload;
      }
      const { assignedSTOItems } = state;
      const stoItem: Record<string, STOItem> = {};
      const skuList: string[] = [];
      assignedSTOItems.forEach((item) => {
        stoItem[item.sku_code] = item;
        skuList.push(item.sku_code);
      });
      state.stoItems = stoItem;
      state.stoItemSKUList = skuList;
    },
    updateSTOReceiveItemSKUList(
      state: STOReceiveItemState,
      action: { type: string; payload: string[] },
    ) {
      state.stoItemSKUList = action.payload;
    },
    setSTOReceiveItem(
      state: STOReceiveItemState,
      action: { type: string; payload: STOItem | null },
    ) {
      state.stoReceiveItem = action.payload;
    },
    setSTOItemReceiveActual(
      state: STOReceiveItemState,
      action: {
        type: string;
        payload: { isRemove: boolean; item: STOProcessedItem; itemIdx: number };
      },
    ) {
      const { isRemove, item, itemIdx } = action.payload;
      if (state.assignedSTOItems[itemIdx]) {
        if (isRemove) {
          // pop the pick actual value
          state.assignedSTOItems[itemIdx].receive_actual =
            state.assignedSTOItems[itemIdx].receive_actual.filter(
              (actual) =>
                item.bin !== actual.bin ||
                item.inventory_number !== actual.inventory_number,
            );
        } else {
          // replace/add the value
          const isExist = state.assignedSTOItems[itemIdx].receive_actual.find(
            (actual) =>
              item.bin === actual.bin &&
              item.inventory_number === actual.inventory_number,
          );
          if (isExist) {
            state.assignedSTOItems[itemIdx].receive_actual =
              state.assignedSTOItems[itemIdx].receive_actual.map((actual) =>
                item.bin === actual.bin &&
                item.inventory_number === actual.inventory_number
                  ? item
                  : actual,
              );
          } else {
            state.assignedSTOItems[itemIdx].receive_actual.push(item);
          }
        }
      }
    },
    setSTOReceiveBinRecommendationInfo(
      state: STOReceiveItemState,
      action: { type: string; payload: ProductDetail[] },
    ) {
      state.binRecommendationInfo = action.payload;
    },
  },
});

export const {
  setAssignedSTOReceiveItem,
  addAssignedSTOReceiveItem,
  updateAssignedSTOReceiveItem,
  removeAssignedSTOReceiveItem,
  prepareSTOReceiveItems,
  updateSTOReceiveItemSKUList,
  setSTOItemReceiveActual,
  setSTOReceiveItem,
  setSTOReceiveBinRecommendationInfo,
} = stoReceiveItemSlice.actions;

export default stoReceiveItemSlice.reducer;
